import React from "react";
import { DispatchProp } from "react-redux";
import { QuestionModel } from "../../models/QuestionModel";
import { BaseControl, ControlProps } from "./BaseControl";
import { ChoiceModel } from "../../models/ChoiceModel";
import { getSettingString } from "../../models/utilities/settingUtilities";
import { translate } from "../../models/utilities/translationUtilities";
import { getRendererProps } from "../renderers/BaseRenderer";
import HeatMapRenderer from "../renderers/v2/HeatMapRenderer";
import HeatMapSelectionRenderer from "../renderers/v2/HeatMapSelectionRenderer";

interface Props extends ControlProps, DispatchProp {}

interface State {
    value: string;
    focus: boolean;
    answers: ChoiceModel[];
    input?: HTMLElement;
}

export class HeatMapControl extends BaseControl<Props, State> {
    onSelectedHeatMapArea(event: any, variableName = "") {
        var bounds = event.target.getBoundingClientRect();
        var x = event.clientX - bounds.left;
        var y = event.clientY - bounds.top;
        var value = x.toFixed(4) + "," + y.toFixed(4);
        if (!this.onSelectDot) {
            this.setState((prevState) => {
                const newState = { ...prevState };
                if (!newState.value) {
                    newState.value = value;
                } else {
                    newState.value = newState.value + "|" + value;
                }
                return newState;
            });
            this.setMentions(this.props, variableName, (m) => {
                return m.add(value);
            });

            if (
                this.renderHeatMapPreview[variableName] &&
                this.renderHeatMapPreview[variableName].length > 0
            ) {
                this.renderHeatMapPreview[variableName].push(value);
            } else {
                this.renderHeatMapPreview[variableName] = [];
                this.renderHeatMapPreview[variableName].push(value);
            }
        }
        this.onSelectDot = false;
    }

    onSelectDot = false;
    onSelectedHeatMapAreaOnDot(index, variableName) {
        this.onSelectDot = true;
        this.setState((prevState) => {
            const newState = { ...prevState };
            var totalVal = newState.value ? newState.value.split("|") : [];
            if (totalVal.length > 0) totalVal.splice(index - 1, 1);
            if (totalVal.length != 0) {
                newState.value = totalVal.join("|");
            } else {
                newState.value = undefined;
            }
            return newState;
        });
        var mentions = this.getMentions(this.props);
        var selectedMentions = mentions.filter((m) => m.index === index);
        if (selectedMentions.length > 0) {
            this.setMentions(this.props, variableName, (m) =>
                m.removeValue(selectedMentions[0].value),
            );
        }
    }

    getDimension(value: any) {
        return Number.isNaN(value) ? "0" : `${value}px`;
    }

    renderMapChoice(question: QuestionModel, variableName: any, choice: any, index = 1) {
        var dataPoint = choice.split(",");
        return (
            <HeatMapSelectionRenderer
                {...getRendererProps(this.props)}
                key={index}
                top={this.getDimension(dataPoint[1])}
                left={this.getDimension(dataPoint[0])}
                color={getSettingString(question, "OutlineColor")}
                onSelect={(divIndex) => this.onSelectedHeatMapAreaOnDot(divIndex, variableName)}
                index={index}
                borderWidth={getSettingString(question, "OutlineWeight")}
                variableName={variableName}
            />
        );
    }

    renderHeatMapPreview = {};
    renderMap(question: QuestionModel) {
        var variableName = question.variables.length > 0 ? question.variables[0].name : "";
        var dataPoints = this.state?.value?.replace("undefined", "").split("|");
        var mentions = this.getMentions(this.props, variableName);
        if (mentions && mentions.length > 0) {
            dataPoints = [];
            mentions.map((mention) => {
                dataPoints.push(mention.value);
            });
        }
        return dataPoints?.map((answer, index) =>
            this.renderMapChoice(question, variableName, answer, index + 1),
        );
    }

    updateState(props: Props) {
        this.setState((prevState) => {
            const newState = { ...prevState };
            const mention = this.getMention(props);
            if (!newState.value) {
                newState.value = mention?.value;
            } else {
                newState.value = newState.value + "|" + mention?.value;
            }
            return newState;
        });
    }

    renderQuestion(question: QuestionModel) {
        const { language } = this.props;
        var variableName = question.variables.length > 0 ? question.variables[0].name : "";
        if (variableName) {
            return (
                <HeatMapRenderer
                    {...getRendererProps(this.props)}
                    imageSrc={translate(question, "ImageUrl", language)}
                    height={getSettingString(question, "Height")}
                    width={getSettingString(question, "Width")}
                    onSelect={(event) => this.onSelectedHeatMapArea(event, variableName)}
                >
                    {this.renderMap(question)}
                </HeatMapRenderer>
            );
        }
    }
}
